import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Clock, ArrowLeft, CheckCircle2, XCircle } from "lucide-react";

function fmt(s) {
  const m = Math.floor(s / 60);
  const sec = Math.max(0, s) % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export default function Module() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const trackMult = user?.track === "renewal" ? 0.5 : 1;
  const demo = !!window.__demo;
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [phase, setPhase] = useState("read"); // read | quiz | result
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const tickRef = useRef(null);
  const visibleRef = useRef(true);

  // Load
  useEffect(() => {
    Promise.all([api.get("/course"), api.get("/progress")]).then(([c, p]) => {
      setCourse(c.data);
      setProgress(p.data);
      const mod = c.data.modules.find((m) => m.id === id);
      setAnswers(new Array(mod.quiz.length).fill(-1));
    });
  }, [id]);

  // Visibility tracking
  useEffect(() => {
    const handler = () => { visibleRef.current = !document.hidden; };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, []);

  // Tick timer — sends to server every 15s of accumulated visible time
  const tickServer = useCallback(async (seconds) => {
    try {
      const { data } = await api.post(`/progress/module/${id}/time`, { seconds });
      setProgress((p) => p && ({ ...p, modules: { ...p.modules, [id]: { ...p.modules[id], seconds_spent: data.seconds_spent } } }));
    } catch { /* ignore */ }
  }, [id]);

  useEffect(() => {
    if (phase !== "read") return;
    let local = 0;
    tickRef.current = setInterval(() => {
      if (visibleRef.current) {
        local += 1;
        if (local >= 15) {
          tickServer(local);
          local = 0;
        }
      }
    }, 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      if (local > 0) tickServer(local);
    };
  }, [phase, tickServer]);

  if (!course || !progress) return <div className="max-w-3xl mx-auto px-6 py-20 text-slate-500">Loading module…</div>;

  const mod = course.modules.find((m) => m.id === id);
  if (!mod) return <div className="max-w-3xl mx-auto px-6 py-20 text-slate-500">Module not found.</div>;

  const spent = progress.modules[id].seconds_spent;
  const minSec = demo ? 10 : Math.round(mod.min_seconds * trackMult);
  const remaining = Math.max(0, minSec - spent);
  const timeMet = remaining === 0;
  const pct = Math.min(100, Math.round((spent / minSec) * 100));

  const submitQuiz = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/progress/module/${id}/quiz`, { answers });
      setResult(data);
      setPhase("result");
      // refresh progress
      const p = await api.get("/progress");
      setProgress(p.data);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      {/* Sticky timer */}
      <div className="sticky top-16 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
          <button
            data-testid="back-to-dashboard"
            onClick={() => nav("/dashboard")}
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </button>
          <div data-testid="module-timer" className="font-mono font-bold text-sm px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-sm inline-flex items-center gap-2">
            <Clock className="h-4 w-4" />
            {timeMet ? "Time complete" : `${fmt(remaining)} remaining`}
          </div>
        </div>
        <div className="h-1 bg-slate-100 w-full">
          <div className="h-1 bg-slate-900 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
          Module {mod.order} of 6 · {mod.duration_minutes} minutes
        </div>
        <h1 className="text-4xl font-bold tracking-tight text-slate-900 mt-3" style={{ fontFamily: "Chivo, sans-serif" }}>
          {mod.title}
        </h1>
        <p className="text-lg text-slate-600 mt-4 leading-relaxed">{mod.summary}</p>

        {phase === "read" && (
          <>
            <div className="mt-12 space-y-10">
              {mod.sections.map((s, i) => (
                <section key={i}>
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>
                    {s.heading}
                  </h2>
                  <div className="mt-4 text-slate-700 leading-relaxed whitespace-pre-line">{s.body}</div>
                </section>
              ))}
            </div>
            <div className="mt-16 border-t border-slate-200 pt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="text-sm text-slate-600">
                {timeMet
                  ? "Minimum study time complete. You may take the quiz."
                  : `Continue reading. Quiz unlocks after ${fmt(remaining)} more.`}
              </div>
              <button
                data-testid="start-quiz-btn"
                disabled={!timeMet}
                onClick={() => setPhase("quiz")}
                className={`px-6 py-3 rounded-sm font-medium transition-colors ${
                  timeMet ? "bg-slate-900 hover:bg-slate-800 text-white" : "bg-slate-100 text-slate-400 cursor-not-allowed"
                }`}
              >
                Take quiz
              </button>
            </div>
          </>
        )}

        {phase === "quiz" && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>
              Quiz · 5 questions · 80% to pass
            </h2>
            <div className="mt-8 space-y-8">
              {mod.quiz.map((q, qi) => (
                <div key={qi} data-testid={`quiz-q-${qi}`} className="border border-slate-200 rounded-sm p-6">
                  <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Question {qi + 1}</div>
                  <div className="text-lg font-semibold text-slate-900 mt-2">{q.q}</div>
                  <div className="mt-4 space-y-2">
                    {q.options.map((o, oi) => (
                      <label key={oi} data-testid={`quiz-q-${qi}-opt-${oi}`}
                        className={`flex items-start gap-3 px-4 py-3 border rounded-sm cursor-pointer transition-colors ${
                          answers[qi] === oi ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"
                        }`}>
                        <input type="radio" name={`q${qi}`} className="mt-1"
                          checked={answers[qi] === oi}
                          onChange={() => setAnswers((a) => a.map((x, i) => (i === qi ? oi : x)))} />
                        <span className="text-sm text-slate-700">{o}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-center justify-between">
              <button onClick={() => setPhase("read")} className="text-sm text-slate-600 hover:text-slate-900">
                ← Back to reading
              </button>
              <button
                data-testid="submit-quiz-btn"
                disabled={answers.some((a) => a === -1) || submitting}
                onClick={submitQuiz}
                className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-sm font-medium disabled:opacity-50"
              >
                {submitting ? "Submitting…" : "Submit quiz"}
              </button>
            </div>
          </div>
        )}

        {phase === "result" && result && (
          <div className="mt-12">
            <div className={`border rounded-sm p-8 ${result.module_passed ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
              <div className="flex items-start gap-4">
                {result.module_passed ? (
                  <CheckCircle2 className="h-10 w-10 text-green-700" />
                ) : (
                  <XCircle className="h-10 w-10 text-red-700" />
                )}
                <div>
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>
                    {result.module_passed ? "Module Passed" : result.passed ? "Quiz Passed — But Time Not Met" : "Not Yet — Try Again"}
                  </h2>
                  <p className="text-slate-700 mt-2">
                    Score: <span className="font-mono font-bold">{Math.round(result.score * 100)}%</span> ({result.correct} of {result.total})
                    {!result.passed && <> · 80% required</>}
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-8 space-y-4">
              {result.review.map((r, i) => (
                <div key={i} className={`p-4 border rounded-sm ${r.is_correct ? "border-green-200" : "border-red-200"}`}>
                  <div className="text-sm font-semibold text-slate-900">Q{i + 1}. {mod.quiz[i].q}</div>
                  <div className="mt-2 text-sm">
                    <div className={r.is_correct ? "text-green-700" : "text-red-700"}>
                      Your answer: {r.your_answer === -1 ? "—" : mod.quiz[i].options[r.your_answer]}
                    </div>
                    {!r.is_correct && (
                      <div className="text-slate-700 mt-1">Correct: {mod.quiz[i].options[r.correct]}</div>
                    )}
                  </div>
                  {r.explanation && (
                    <div data-testid={`quiz-explanation-${i}`} className="mt-3 bg-slate-50 border-l-2 border-blue-700 pl-3 py-2 text-xs text-slate-700 leading-relaxed">
                      <span className="font-semibold uppercase tracking-[0.1em] text-blue-700">Why</span>
                      <div className="mt-1">{r.explanation}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-center justify-between">
              {result.module_passed ? (
                <Link to="/dashboard" data-testid="back-dashboard-after-pass"
                  className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-sm font-medium">
                  Continue to dashboard
                </Link>
              ) : (
                <button
                  data-testid="retake-quiz-btn"
                  onClick={() => {
                    setAnswers(new Array(mod.quiz.length).fill(-1));
                    setResult(null);
                    setPhase("quiz");
                  }}
                  className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-sm font-medium"
                >
                  Retake quiz
                </button>
              )}
              <Link to="/dashboard" className="text-sm text-slate-600 hover:text-slate-900">Back to dashboard</Link>
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
